const colormap = colorMap();

export function hsl_to_rgb(h: number, s: number, l: number) {
  function hue_to_rgb(p: any, q: any, t: any) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return {
    r: hue_to_rgb(p, q, h + 1 / 3),
    g: hue_to_rgb(p, q, h),
    b: hue_to_rgb(p, q, h - 1 / 3),
  };
}

export function colorMap() {
  const length = 65535;
  const ret = {
    length: length - 1,
    r: new Uint8ClampedArray(length),
    g: new Uint8ClampedArray(length),
    b: new Uint8ClampedArray(length),
  };

  function sigmoid(x: any, phi: any, tau: any) {
    return 1.0 / (1 + Math.exp(-(x - phi) / tau));
  }

  for (let i = 0; i < length; i++) {
    const x = i / length;

    const hue = (1 - x) * 0.66;

    let lum = 0.5 * sigmoid(x, 0.05, 0.02);
    lum *= 0.1 + 0.9 * x;

    const rgb = hsl_to_rgb(hue, 0.85, lum);

    ret.r[i] = 255 * rgb.r;
    ret.g[i] = 255 * rgb.g;
    ret.b[i] = 255 * rgb.b;
  }

  return ret;
}

export function clearCanvas(ctx: CanvasRenderingContext2D) {
  if (!ctx) return;
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
}

export function drawChartLabels(
  ctx: CanvasRenderingContext2D,
  analyzerName: string | undefined,
  snapshotName: string | undefined,
  ref: number | undefined,
  scale: number | undefined
) {
  drawColorMap(ctx);
  drawAmplitudeLabels(ctx, ref || -30, scale || 10);
  drawTitle(ctx, `${analyzerName} ${snapshotName}`);
}

export function handleResize(
  ctx: CanvasRenderingContext2D,
  analyzerName: string | undefined,
  snapshotName: string | undefined,
  ref: number | undefined,
  scale: number | undefined
) {
  const { height } = ctx.canvas.getBoundingClientRect();
  const floorHeight = Math.floor(height);

  if (ctx.canvas.height !== floorHeight) {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    drawFrame(ctx);
    drawChartLabels(ctx, analyzerName, snapshotName, ref, scale);
  }
}

export function drawColorMap(ctx: CanvasRenderingContext2D) {
  for (let y = 0; y < ctx.canvas.height; y++) {
    const i = Math.round((1 - y / ctx.canvas.height) * colormap.length);

    ctx.fillStyle =
      'rgb(' + colormap.r[i] + ',' + colormap.g[i] + ',' + colormap.b[i] + ')';

    ctx.fillRect(25, y, 11, 1);
  }
}

export function drawFrame(ctx: CanvasRenderingContext2D) {
  const { width, height } = ctx.canvas.getBoundingClientRect();
  const floorHeight = Math.floor(height);
  const floorWidth = Math.floor(width);

  ctx.canvas.width = floorWidth;
  ctx.canvas.height = floorHeight;

  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
}

export function drawTitle(ctx: CanvasRenderingContext2D, title: string) {
  ctx.font = '13px serif';
  ctx.fillStyle = '#FFFFFFFF';
  ctx.fillText(title, 40, 15);
}

export function drawAmplitudeLabels(
  ctx: CanvasRenderingContext2D,
  ref: number,
  scale: number
) {
  ctx.font = '15px serif';

  if (ctx.canvas.height <= 250) {
    ctx.font = '10px serif';
  }

  ctx.fillStyle = '#FFFFFFFF';

  const bottomScale = -(10 * scale) + ref;

  let position = 25;
  for (let i = Number(ref); i >= Number(bottomScale); i -= scale) {
    ctx.fillText(i.toString(), 2, position);
    position += ctx.canvas.height / 11;
  }
}

export function drawSpectrogram(ctx: any, destCtx: CanvasRenderingContext2D) {
  if (!ctx.img) return;

  ctx.putImageData(ctx.img, 0, 0);
  const sx = 0;
  const sy = ctx.img.height - destCtx.canvas.height;
  const sWidth = ctx.img.width;
  const sHeight = destCtx.canvas.height;
  const dx = 36;
  const dy = 0;
  const dWidth = destCtx.canvas.width - 25;
  const dHeight = destCtx.canvas.height;
  destCtx.drawImage(
    ctx.canvas,
    sx,
    sy,
    sWidth,
    sHeight,
    dx,
    dy,
    dWidth,
    dHeight
  );
}

export function clear(ctx: any) {
  if (!ctx.img) return;
  for (let i = 0, len = ctx.img.data.length; i < len; i += 4) {
    ctx.img.data[i] = 0;
    ctx.img.data[i + 1] = 0;
    ctx.img.data[i + 2] = 0;
    ctx.img.data[i + 3] = 0;
  }
}

export function trace_data_shift(ctx: any) {
  if (!ctx.img || !ctx.img.data) return;
  ctx.img.data.set(ctx.img.data.subarray(4 * ctx.img.width), 0);

  let last_row = (ctx.height - 1) * ctx.img.width * 4;
  for (let x = 0; x < ctx.img.width; x++) {
    let img_index = last_row + 4 * x;
    ctx.img.data[img_index + 0] = 0;
    ctx.img.data[img_index + 1] = 0;
    ctx.img.data[img_index + 2] = 0;
    ctx.img.data[img_index + 3] = 0;
  }
}

const normalizeTracePoint = (
  point: number,
  reference: number,
  scale: number
) => {
  let adjustedScale = scale * 10;
  return (point - reference + adjustedScale) / adjustedScale;
};

export function newTraceData(
  new_points: number[],
  ref: number,
  scale: number,
  ctx: any
) {
  if (!new_points || !new_points.length) {
    return;
  }

  if (!ctx.img || ctx.img.width !== new_points.length) {
    ctx.width = new_points.length;
    ctx.height = 1000;

    ctx.img = ctx.getImageData(0, 0, ctx.width, ctx.height);
    ctx.canvas.width = new_points.length;
    ctx.canvas.height = 1000;
    clear(ctx);
  }

  const img_data = ctx.img.data;
  const last_row = (ctx.height - 1) * ctx.img.width * 4;

  for (let x = 0; x < ctx.img.width; x++) {
    const normalizedPoint = normalizeTracePoint(new_points[x], ref, scale);
    const z = Math.max(0, Math.min(1, normalizedPoint));
    let r, g, b, a;
    if (isNaN(z)) {
      // Draw invalid trace points as transparent
      r = g = b = a = 0;
    } else {
      let cm_index = Math.floor(z * colormap.length);
      cm_index = Math.max(0, Math.min(colormap.length, cm_index));
      r = colormap.r[cm_index];
      g = colormap.g[cm_index];
      b = colormap.b[cm_index];
      a = 255;
    }

    let img_index = last_row + 4 * x;
    img_data[img_index + 0] = r;
    img_data[img_index + 1] = g;
    img_data[img_index + 2] = b;
    img_data[img_index + 3] = a;
  }
}
